import React, { useState, useEffect } from 'react';
import { X, Save, Plus, AlertTriangle } from 'lucide-react';

// Strip **Tags:** and **Scores:** lines from the AI text — we render these separately
export function stripRedundantSections(text) {
  if (!text) return '';
  return text
    .replace(/\*\*Scores:\*\*[\s\S]*?(?=\*\*[A-Z]|$)/gi, '')
    .replace(/\*\*Tags:\*\*[\s\S]*?(?=\*\*[A-Z]|$)/gi, '')
    .trim();
}

// Parse **Section:** narrative blocks
export function parseSections(text) {
  const sections = [];
  const sectionRegex = /\*\*([^*]+)\*\*[:\s]*([\s\S]*?)(?=\*\*[^*]+\*\*|$)/g;
  let match;
  while ((match = sectionRegex.exec(text)) !== null) {
    const heading = match[1].trim();
    const content = match[2].trim();
    if (content) sections.push({ heading, content });
  }
  return sections;
}

// Build human-readable tag pills from cortex_tags
export function buildTagPills(cortex_tags) {
  if (!cortex_tags) return [];
  const pills = [];

  const {
    emotional_state,
    followed_rules,
    fomo_detected,
    confidence_level,
    revenge_trade_signal,
    exit_discipline,
    patience_score,
    key_themes,
  } = cortex_tags;

  // Emotional state
  const emotionColors = {
    calm: 'bg-green-900/40 border-green-600/40 text-green-300',
    confident: 'bg-blue-900/40 border-blue-600/40 text-blue-300',
    excited: 'bg-yellow-900/40 border-yellow-600/40 text-yellow-300',
    anxious: 'bg-orange-900/40 border-orange-600/40 text-orange-300',
    frustrated: 'bg-red-900/40 border-red-600/40 text-red-300',
    neutral: 'bg-secondary border-border text-muted-foreground',
  };
  if (emotional_state) {
    pills.push({
      label: `Mood: ${emotional_state.charAt(0).toUpperCase() + emotional_state.slice(1)}`,
      color: emotionColors[emotional_state] || emotionColors.neutral,
    });
  }

  // Exit discipline
  const exitColors = {
    good: 'bg-green-900/40 border-green-600/40 text-green-300',
    mixed: 'bg-yellow-900/40 border-yellow-600/40 text-yellow-300',
    poor: 'bg-red-900/40 border-red-600/40 text-red-300',
  };
  if (exit_discipline) {
    const label = exit_discipline === 'good' ? 'Clean Exit' : exit_discipline === 'poor' ? 'Exit: Deviated' : 'Exit: Mixed';
    pills.push({ label, color: exitColors[exit_discipline] || exitColors.mixed });
  }

  // Confidence
  if (confidence_level != null) {
    const conf = Number(confidence_level);
    const color = conf >= 7 ? 'bg-blue-900/40 border-blue-600/40 text-blue-300'
      : conf >= 4 ? 'bg-secondary border-border text-muted-foreground'
      : 'bg-orange-900/40 border-orange-600/40 text-orange-300';
    pills.push({ label: `Confidence ${conf}/10`, color });
  }

  // Patience
  if (patience_score != null) {
    const pat = Number(patience_score);
    const color = pat >= 7 ? 'bg-green-900/40 border-green-600/40 text-green-300'
      : pat >= 4 ? 'bg-secondary border-border text-muted-foreground'
      : 'bg-orange-900/40 border-orange-600/40 text-orange-300';
    pills.push({ label: `Patience ${pat}/10`, color });
  }

  // Rules followed — only show if broken
  if (followed_rules === false) {
    pills.push({ label: 'Rules Broken', color: 'bg-red-900/40 border-red-600/40 text-red-300' });
  } else if (followed_rules === true) {
    pills.push({ label: 'Rules Followed', color: 'bg-green-900/40 border-green-600/40 text-green-300' });
  }

  // FOMO — only show if detected
  if (fomo_detected === true) {
    pills.push({ label: 'FOMO Detected', color: 'bg-orange-900/40 border-orange-600/40 text-orange-300' });
  }

  // Revenge trade — only show if detected
  if (revenge_trade_signal === true) {
    pills.push({ label: 'Revenge Trade', color: 'bg-red-900/40 border-red-600/40 text-red-300' });
  }

  // Key themes
  const themeList = Array.isArray(key_themes) ? key_themes : [];
  themeList.forEach(theme => {
    if (theme && typeof theme === 'string') {
      pills.push({
        label: theme.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        color: 'bg-secondary border-border text-muted-foreground',
      });
    }
  });

  return pills;
}

export function ScoreBar({ label, value, max = 100 }) {
  const pct = Math.round((value / max) * 100);
  const color = pct >= 80 ? '#22c55e' : pct >= 60 ? '#eab308' : '#ef4444';
  return (
    <div className="flex-1 min-w-[150px]">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-muted-foreground font-medium">{label}</span>
        <span className="text-xs font-bold" style={{ color }}>{value}/{max}</span>
      </div>
      <div className="h-2 bg-secondary rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

export function TagPill({ label, color }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${color}`}>
      {label}
    </span>
  );
}

const sectionColors = {
  'Before Trade': 'border-l-blue-500 bg-blue-500/5',
  'During Trade': 'border-l-yellow-500 bg-yellow-500/5',
  'After Trade':  'border-l-green-500 bg-green-500/5',
};

export function SectionBlock({ heading, content }) {
  const style = sectionColors[heading] || 'border-l-border bg-secondary/50';
  return (
    <div className={`rounded-r-lg border-l-4 p-4 ${style}`}>
      <h3 className="text-sm font-semibold text-card-foreground mb-1">{heading}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{content}</p>
    </div>
  );
}

export const SummaryModal = ({
  ai_summary,
  onMoreDetails,
  onAddMore,
  onEndSession,
  isRequestingDetails = false,
  isEndingSession = false
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const hasContent = ai_summary && ai_summary.ai_summary && ai_summary.ai_summary.length > 1;
    setIsVisible(hasContent);
  }, [ai_summary]);

  const handleClose = () => setIsVisible(false);
  const handleAddMore = () => { setIsVisible(false); onAddMore && onAddMore(); };
  const handleEndSession = () => { setIsVisible(false); onEndSession && onEndSession(); };

  if (!isVisible || ai_summary === null) return null;

  const cleanText = stripRedundantSections(ai_summary.ai_summary);
  const sections = parseSections(cleanText);
  const summaryPassed = ai_summary.summaryPassed;

  // Use structured scores from API, not parsed text
  const scores = ai_summary.scores;
  const cortex_tags = ai_summary.cortex_tags;
  const tagPills = buildTagPills(cortex_tags);
  const ruleBreaks = cortex_tags?.rule_breaking_phrases?.filter(r => r?.phrase) || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4 border border-border">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-bold text-card-foreground">AI Summary</h2>
          <button onClick={handleClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Narrative sections */}
        {sections.length > 0 ? (
          <div className="space-y-3 mb-5">
            {sections.map((s, i) => <SectionBlock key={i} heading={s.heading} content={s.content} />)}
          </div>
        ) : (
          <div className="mb-5 p-4 bg-secondary rounded-lg border border-border">
            <p className="text-sm text-foreground whitespace-pre-line">{cleanText}</p>
          </div>
        )}

        {/* Scores */}
        {scores && (
          <div className="mb-5 p-4 bg-secondary rounded-lg border border-border">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Performance Scores</h3>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {scores.emotionalState != null && <ScoreBar label="Emotional Control" value={scores.emotionalState} />}
              {scores.riskManagement != null && <ScoreBar label="Risk Management" value={scores.riskManagement} />}
              {scores.strategyAdherence != null && <ScoreBar label="Strategy Adherence" value={scores.strategyAdherence} />}
            </div>
          </div>
        )}

        {/* Behavioral tags */}
        {tagPills.length > 0 && (
          <div className="mb-5">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Behavioral Insights</h3>
            <div className="flex flex-wrap gap-2">
              {tagPills.map((t, i) => <TagPill key={i} label={t.label} color={t.color} />)}
            </div>
          </div>
        )}

        {/* Rule-breaking phrases */}
        {ruleBreaks.length > 0 && (
          <div className="mb-5 p-3 bg-red-900/10 border border-red-900/30 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
              <h3 className="text-xs font-semibold text-red-400 uppercase tracking-wider">Rule Violations</h3>
            </div>
            <div className="space-y-2">
              {ruleBreaks.map((r, i) => (
                <div key={i} className="text-xs text-muted-foreground">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full border mr-2 font-medium ${
                    r.severity === 'high' ? 'bg-red-900/40 border-red-600/40 text-red-300'
                    : r.severity === 'medium' ? 'bg-orange-900/40 border-orange-600/40 text-orange-300'
                    : 'bg-yellow-900/40 border-yellow-600/40 text-yellow-300'
                  }`}>
                    {r.tag?.replace(/_/g, ' ')}
                  </span>
                  <span className="italic">"{r.phrase}"</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {summaryPassed === false && (
          <div className="mb-4 p-2 bg-yellow-900/20 border-l-4 border-yellow-500 text-yellow-400">
            <p className="text-sm font-medium">⚠️ Summary needs more details before saving</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleAddMore}
            className="flex items-center justify-center px-4 py-2 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80 border border-border"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add More
          </button>

          {summaryPassed === true && (
            <button
              onClick={handleEndSession}
              className="flex items-center justify-center px-4 py-2 rounded bg-crimson text-white hover:bg-crimson/90"
            >
              <Save className="w-4 h-4 mr-2" />
              {isEndingSession ? 'Ending Session...' : 'Save AI Summary'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
